<template>
  <View gap="sm" direction="column">
    <View gap="md" direction="row" justify="space-between" padding-block="sm">
      <ScrollArea>
        <View gap="md" padding-block="sm" role="tablist">
          <Text
            variant="h3"
            as="button"
            class="tab"
            role="tab"
            wrap="nowrap"
            v-for="section in sections"
            :tabindex="0"
            :aria-selected="activeTab === section"
            :key="section"
            :color="activeTab === section ? 'primary' : 'tertiary'"
            @click="setActiveTab(section)"
          >
            {{ section }}
          </Text>
        </View>
      </ScrollArea>
      <ShowMoreButton v-if="hasMoreDataThanLimit" @click="openModal" />
    </View>

    <PlaceholderSection
      v-if="isPending.value || items.length === 0"
      :is-loading="isPending.value"
      :icon="'clock-rewind'"
      title="No data yet"
      description="Properties that need attention or are gaining momentum will appear here"
      class="no-data-placeholder"
    />

    <PortfolioPerformanceList v-else :items="items" />

    <PortfolioPerformanceModal ref="portfolioPerformanceModalRef" />
  </View>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { ScrollArea, Text, View } from '@tallo/design-system';
import {
  PortfolioPerformanceList,
  PortfolioPerformanceModal,
  PortfolioPerformanceTabs,
  useUnitPerformanceComposable,
} from '@tallo/investor/portfolio-statistics';

import PlaceholderSection from './placeholder-section.vue';
import ShowMoreButton from './show-more-button.vue';

const limit = 2;
const sections = Object.values(PortfolioPerformanceTabs);

const { activeTab, isPending, data, setActiveTab } = useUnitPerformanceComposable();

const units = computed(() => data.value || []);
const items = computed(() => units.value.slice(0, limit));
const hasMoreDataThanLimit = computed(() => units.value.length > limit);
const portfolioPerformanceModalRef = ref<InstanceType<typeof PortfolioPerformanceModal>>();

function openModal() {
  portfolioPerformanceModalRef.value?.open(activeTab.value);
}
</script>

<style scoped lang="scss">
.tab {
  padding: 0;
  cursor: pointer;
  appearance: none;
  border: none;
  background: transparent;
}

.no-data-placeholder {
  min-height: 15rem;
}
</style>
